/**
 * cross-tab serialize helper (review BLOCK-C — Pre-mortem 보강).
 *
 * navigator.locks (Web Locks API) 로 같은 origin 의 모든 탭이 공유하는 mutex 제공.
 * 다중 탭 동시 cold-start / 동시 401 race 시 한 탭만 fetch 진입, 나머지는 대기 후
 * 결과를 store 에서 재확인.
 *
 * 호환성:
 *   - Chrome 69+, Firefox 96+, Safari 15.4+ (modern browsers 필수)
 *   - 미지원 환경 (SSR, Node test, 구버전 browser) 은 fallback 으로 즉시 callback 실행
 *     (기존 module-level promise 와 동등 동작 — 단일 탭 가정)
 *
 * 사용 예:
 *   await withCrossTabLock('workchat:auth:refresh', async () => {
 *     await fetch('/api/v1/auth/token/refresh', ...);
 *   });
 */

interface NavigatorLocks {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
  request<T>(name: string, options: { mode?: 'exclusive' | 'shared'; ifAvailable?: boolean }, callback: () => Promise<T>): Promise<T>;
}

function getNavigatorLocks(): NavigatorLocks | null {
  if (typeof navigator === 'undefined') return null;
  const locks = (navigator as Navigator & { locks?: NavigatorLocks }).locks;
  return locks ?? null;
}

/**
 * `name` 키의 cross-tab exclusive lock 안에서 callback 을 실행.
 * 다른 탭이 같은 lock 을 보유 중이면 await — 보유 후 본 탭이 lock 획득.
 *
 * 미지원 환경은 lock 없이 즉시 callback 실행. 호출자는 lock 안에서도 store 를 다시
 * 확인해 다른 탭이 이미 처리했는지 (e.g. user 가 set 됐는지) 판단해야 함 — 본 함수는
 * mutex 만 제공, 결과 dedup 은 호출자 책임.
 */
export async function withCrossTabLock<T>(name: string, callback: () => Promise<T>): Promise<T> {
  const locks = getNavigatorLocks();
  if (!locks) {
    return callback();
  }
  return locks.request(name, callback);
}

export const LOCK_AUTH_REFRESH = 'workchat:auth:refresh';
export const LOCK_AUTH_PROBE = 'workchat:auth:probe';
